"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { RIS_FILE_VALIDATOR_STRINGS } from "@/components/tools/ris-file-validator";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  FieldGroup,
  RisChange,
  RisFixKind,
  RisFixOptions,
  RisIssue,
  RisIssueCode,
  RisSeverity,
} from "@/lib/tool-kit/identifiers/ris-check";

/*
 * The RIS File Validator's report: the problems found, the changes the cleaner made,
 * and the same two lists as a plain-text file. Every word comes from the widget's
 * strings.
 */

type Strings = typeof RIS_FILE_VALIDATOR_STRINGS;
type Format = (value: number) => string;

/** Lists longer than this are cut on screen; the downloaded report has every line. */
export const SCREEN_LIMIT = 200;

const ISSUE_KEY: Record<RisIssueCode, keyof Strings> = {
  "no-records": "issueNoRecords",
  "bom-start": "issueBomStart",
  "bom-stray": "issueBomStray",
  mojibake: "issueMojibake",
  "replacement-char": "issueReplacementChar",
  "line-endings-mixed": "issueEndingsMixed",
  "line-endings-lf": "issueEndingsLf",
  "line-endings-cr": "issueEndingsCr",
  "malformed-tag": "issueMalformedTag",
  "unknown-tag": "issueUnknownTag",
  "outside-record": "issueOutsideRecord",
  "missing-ty": "issueMissingTy",
  "missing-er": "issueMissingEr",
  "unknown-type": "issueUnknownType",
  "missing-fields": "issueMissingFields",
  "no-year": "issueNoYear",
  "year-format": "issueYearFormat",
  "author-list": "issueAuthorList",
  "er-value": "issueErValue",
  "jo-full-title": "issueJoFullTitle",
  "jo-repeats-title": "issueJoRepeatsTitle",
  "jo-looks-full": "issueJoLooksFull",
  "duplicate-exact": "issueDuplicateExact",
  "duplicate-doi": "issueDuplicateDoi",
  "duplicate-title": "issueDuplicateTitle",
};

const FIELD_KEY: Record<FieldGroup, keyof Strings> = {
  title: "fieldTitle",
  author: "fieldAuthor",
  creator: "fieldCreator",
  journal: "fieldJournal",
  bookTitle: "fieldBookTitle",
  conference: "fieldConference",
  year: "fieldYear",
  publisher: "fieldPublisher",
  url: "fieldUrl",
};

const SEVERITY_KEY: Record<RisSeverity, keyof Strings> = {
  error: "severityError",
  warning: "severityWarning",
  info: "severityInfo",
};

const SEVERITY_TONE: Record<RisSeverity, string> = {
  error: "font-medium text-destructive",
  warning: "font-medium text-amber-700 dark:text-amber-300",
  info: "font-medium text-muted-foreground",
};

const CHANGE_KEY: Record<RisChange["kind"], keyof Strings> = {
  spacing: "changeSpacing",
  bom: "changeBom",
  "line-endings": "changeLineEndings",
  "end-added": "changeEndAdded",
  "jo-moved": "changeJoMoved",
  "jo-dropped": "changeJoDropped",
  "duplicate-dropped": "changeDuplicateDropped",
};

const FIX_OPTION: Record<RisFixKind, keyof RisFixOptions> = {
  normalize: "normalize",
  "add-end": "addEnd",
  "move-jo": "moveJo",
  "drop-duplicates": "dropDuplicates",
};

/** "Record 3, line 27", "Line 4", or "Whole file". */
function whereText(
  place: { record?: number; line?: number },
  strings: Strings,
  fmt: Format
): string {
  if (place.record !== undefined && place.line !== undefined) {
    return t(strings.whereRecordLine, {
      record: fmt(place.record),
      line: fmt(place.line),
    });
  }
  return place.line === undefined
    ? strings.whereFile
    : t(strings.whereLine, { line: fmt(place.line) });
}

export function issueText(
  issue: RisIssue,
  strings: Strings,
  fmt: Format
): string {
  const detail = issue.detail;
  if (issue.code === "missing-fields") {
    const fields = detail.fields
      .split(",")
      .map((group) => strings[FIELD_KEY[group as FieldGroup]])
      .join(", ");
    return detail.type
      ? t(strings.issueMissingFields, { type: detail.type, fields })
      : t(strings.issueMissingFieldsUntyped, { fields });
  }
  if (issue.code === "unknown-type" && detail.suggestion) {
    return t(strings.issueUnknownTypeSuggestion, detail);
  }
  const of = detail.of ? fmt(Number(detail.of)) : "";
  return t(strings[ISSUE_KEY[issue.code]], { ...detail, of });
}

function changeText(
  change: RisChange,
  strings: Strings,
  fmt: Format,
  locale: string
): string {
  if (change.kind === "line-endings") {
    const count = Number(change.detail.count ?? 0);
    return tPlural(strings.changeLineEndings, locale, count, {
      count: fmt(count),
    });
  }
  return t(strings[CHANGE_KEY[change.kind]], {
    record: change.record === undefined ? "" : fmt(change.record),
    of: change.detail.of ? fmt(Number(change.detail.of)) : "",
    line: change.line === undefined ? "" : fmt(change.line),
    to: change.detail.to ? fmt(Number(change.detail.to)) : "",
  });
}

/** Whether the cleaned file already repairs this problem. */
export function isFixed(issue: RisIssue, options: RisFixOptions): boolean {
  return issue.fix !== undefined && options[FIX_OPTION[issue.fix]];
}

export function IssueList({
  issues,
  options,
  strings,
}: {
  issues: RisIssue[];
  options: RisFixOptions;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <ul className="space-y-2 text-sm">
      {issues.slice(0, SCREEN_LIMIT).map((issue, index) => (
        // Issues are positional: two can share a code, a record and a line.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional list
        <li className="rounded-lg border border-border bg-card p-3" key={index}>
          <span className={SEVERITY_TONE[issue.severity]}>
            {strings[SEVERITY_KEY[issue.severity]]}
          </span>{" "}
          <span className="text-muted-foreground">
            {whereText(issue, strings, fmt)}
          </span>
          <p className="mt-1 whitespace-pre-wrap break-words text-foreground">
            {issueText(issue, strings, fmt)}
          </p>
          {isFixed(issue, options) ? (
            <p className="mt-1 text-emerald-700 text-xs dark:text-emerald-400">
              {strings.fixedTag}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** A line of RIS as written: monospace, with every space kept — the spacing is the point. */
const RAW_LINE = `${TD} whitespace-pre-wrap break-all font-mono text-xs`;

export function ChangeTable({
  changes,
  strings,
}: {
  changes: RisChange[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const head = [
    strings.colWhere,
    strings.colChange,
    strings.colBefore,
    strings.colAfter,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {changes.slice(0, SCREEN_LIMIT).map((change, index) => (
            // Changes are positional: a line can change twice (a BOM and its spacing).
            // biome-ignore lint/suspicious/noArrayIndexKey: positional list
            <tr key={index}>
              <td className={`${TD} whitespace-nowrap`}>
                {whereText(change, strings, fmt)}
              </td>
              <td className={TD}>{changeText(change, strings, fmt, locale)}</td>
              <td className={RAW_LINE}>{change.before}</td>
              <td className={RAW_LINE}>{change.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Both lists as a plain-text file, nothing cut. */
export function reportText(
  issues: RisIssue[],
  changes: RisChange[],
  strings: Strings,
  fmt: Format,
  locale: string
): string {
  const lines = [strings.reportTitle, "", strings.issuesHeading];
  for (const issue of issues) {
    lines.push(
      `${whereText(issue, strings, fmt)} — ${strings[SEVERITY_KEY[issue.severity]]}: ${issueText(issue, strings, fmt)}`
    );
  }
  lines.push("", strings.changesHeading);
  for (const change of changes) {
    const what = changeText(change, strings, fmt, locale);
    const edit =
      change.before || change.after
        ? `: ${change.before} → ${change.after}`
        : "";
    lines.push(`${whereText(change, strings, fmt)} — ${what}${edit}`);
  }
  return `${lines.join("\r\n")}\r\n`;
}
